import { abs } from "@suse-o11y/abs";

import { differenceInMinutes, isSameDay, startOfDay } from "../date-fns";
import type { OpenInterval } from "../open-interval";
import { startOfNextDay } from "../start-of-next-day";
import type { Time } from "../time";

import { HourMinutes, HourMinutesSeconds, HourMinutesSecondsMillis, getClockTimeFormat } from "./clock-time-format";
import { type CombinedDateTimeFormatWithTime, getCombinedDateTimeFormat } from "./combined-date-time-format";
import { getRelativeDateFormat } from "./date-format";
import type { RelativeTime } from "./relative-time";

export interface FormatViewIntervalProps {
	interval: OpenInterval;
	viewTime: Time;
	/**
	 * TODO STAC-22104 make now required - extends RelativeTimeOptions
	 */
	now?: Time;
	millisBreakPointInMinutes?: number;
	secondsBreakPointInMinutes?: number;
	alwaysUseSecondsFormat?: boolean;
}

/**
 * Formats an interval for views using relative time formatting rules.
 *
 * @param secondsBreakPointInMinutes - by default is 15 minutes, if interval is more than this value it will use HourMinutes format,
 * if less - than HourMinutesSeconds
 * @param alwaysUseSecondsFormat - flag to specify that TimeFormat always requires to keep seconds in it's format
 */
export function formatViewInterval({
	viewTime,
	now = Date.now(),
	interval: [start, end = now],
	millisBreakPointInMinutes = 3,
	secondsBreakPointInMinutes = 15,
	alwaysUseSecondsFormat = false,
}: FormatViewIntervalProps): CombinedDateTimeFormatWithTime {
	const relativeDateFormat = isSameDay(now, viewTime, start, end)
		? undefined
		: getRelativeDateFormat({ viewTime, now }, start, end);

	const relative =
		relativeDateFormat === undefined
			? ({
					expiration: startOfNextDay(now),
					range: [startOfDay(now), startOfNextDay(now)],
				} satisfies RelativeTime)
			: relativeDateFormat.relative;

	const deltaMinutes = abs(differenceInMinutes(start, end));
	const clockTimeFormatDef =
		deltaMinutes <= millisBreakPointInMinutes && !relativeDateFormat
			? HourMinutesSecondsMillis
			: alwaysUseSecondsFormat || deltaMinutes <= secondsBreakPointInMinutes
				? HourMinutesSeconds
				: HourMinutes;

	const formatter = getCombinedDateTimeFormat(relativeDateFormat, getClockTimeFormat(clockTimeFormatDef));
	const format = (t: Time) => formatter.format(t);

	return {
		...formatter,
		relative,
		format,
		toString: () => `${format(start)} - ${format(end)}`,
	};
}
